import styled, { css } from 'styled-components';
import { Avatar } from '../avatar/Avatar';
import { Initials } from '../initials/Initials';
import { Icon } from '../icon/Icon';

export type AchievementMessage = {
	text: string;
	authorName: string;
	localizedDate: string;
	authorAvatarUrl?: string;
}

export type DescribedAchievement = {
	id: string;
	name: string;
	localizedDate: string;
	description?: string;
	/** Dims the timeline dot when `false`. Unset counts as active. */
	active?: boolean;
	message?: AchievementMessage;
}

export type AchievementDescriptionProps = {
	/** Every badge of one achievement type; the first one names the card. */
	achievements: DescribedAchievement[];
	/** Shown above each personal message. ui has no i18n: pass it translated. */
	privacyNotice?: string;
}

const LINE_HEIGHT = 20;
const DOT = 10;
const DOT_GAP = 12;
const LINE = 2;
const ITEM_GAP = 12;

const Container = styled.div`
	width: 300px;
	font-size: 14px;
	line-height: ${LINE_HEIGHT}px;
	overflow-wrap: break-word;
`;

const Header = styled.div`
	display: flex;
	flex-direction: column;
	gap: 6px;
	padding: 16px 20px 0;
`;

const Title = styled.div`
	color: ${props => props.theme.text.primary};
	font-size: 16px;
	line-height: 24px;
	font-weight: 510;
`;

const Subtitle = styled.div`
	color: ${props => props.theme.text.tertiary};
`;

const Timeline = styled.div`
	display: flex;
	flex-direction: column;
	gap: ${ITEM_GAP}px;
	max-height: 200px;
	overflow: auto;
	padding: 16px 20px;
`;

// Dot centred on the date line.
const DOT_TOP = (LINE_HEIGHT - DOT) / 2;

// The rail runs dot centre to dot centre (the opaque dot sits on top of it), and on
// the last item down past its body so the message reads as hanging off its date.
// One rail per item, never two: the token is translucent and overlaps would show.
const rail = css`
	content: '';
	position: absolute;
	left: ${(DOT - LINE) / 2}px;
	width: ${LINE}px;
	top: ${DOT_TOP + DOT / 2}px;
	background: ${props => props.theme.border.decorative};
`;

const Body = styled.div`
	display: flex;
	flex-direction: column;
	gap: 8px;
	margin-top: 8px;
`;

const Item = styled.div<{ $active: boolean }>`
	position: relative;
	padding-left: ${DOT + DOT_GAP}px;
	color: ${props => props.theme.text.secondary};

	&::after {
		content: '';
		position: absolute;
		left: 0;
		top: ${DOT_TOP}px;
		width: ${DOT}px;
		height: ${DOT}px;
		border-radius: 50%;
		background: ${props => props.$active ? props.theme.text.primary : props.theme.text.tertiary};
	}

	&:not(:last-child)::before {
		${rail};
		bottom: -${ITEM_GAP + DOT_TOP + DOT / 2}px;
	}

	&:last-child > ${Body}::before {
		${rail};
		bottom: 0;
	}
`;

const PrivacyNotice = styled.div`
	display: flex;
	align-items: center;
	gap: 6px;
	padding: 6px 8px;
	border-radius: 4px;
	background: ${props => props.theme.fill.dimmed};
	color: ${props => props.theme.text.tertiary};
	font-size: 12px;
	line-height: 16px;
`;

const Quote = styled.div`
	quotes: '“' '”';

	&::before {
		content: open-quote;
	}

	&::after {
		content: close-quote;
	}
`;

const Byline = styled.div`
	display: flex;
	align-items: center;
	gap: 6px;
`;

const MessageDate = styled.span`
	color: ${props => props.theme.text.tertiary};
`;

// Member pictures the app hands out when nobody uploaded one; initials read better.
const isDefaultAvatar = (url?: string) =>
	!url || url.startsWith('/static/img/avatar-member') || url.startsWith('/static/img/avatar-team');

function Message({ text, authorName, localizedDate, authorAvatarUrl }: AchievementMessage) {
	return <>
		<Quote>{text}</Quote>
		<Byline title={authorName}>
			{isDefaultAvatar(authorAvatarUrl)
				? <Initials name={authorName} square />
				: <Avatar url={authorAvatarUrl as string} square />}
			<span>{authorName}</span>
			<MessageDate>{localizedDate}</MessageDate>
		</Byline>
	</>;
}

export function AchievementDescription({ achievements, privacyNotice }: AchievementDescriptionProps) {
	const { name, description } = achievements[0];

	return <Container>
		<Header>
			<Title>{name}</Title>
			{description && <Subtitle>{description}</Subtitle>}
		</Header>
		<Timeline>
			{achievements.map(({ id, active = true, localizedDate, message }) =>
				<Item key={id} $active={active}>
					{localizedDate}
					{message && <Body>
						{privacyNotice && <PrivacyNotice><Icon icon="visible" />{privacyNotice}</PrivacyNotice>}
						<Message {...message} />
					</Body>}
				</Item>,
			)}
		</Timeline>
	</Container>;
}
